// 1.47.0.0 (demande du club "suivre les usages du backoffice... de maniere anonyme, un role me
// suffit") : mesure d'audience INTERNE - aucun cookie, aucun service tiers. Le navigateur note :
//   - page     : changement de page, sous un nom generique ("Fiche plongeur", jamais l'id) ;
//   - onglet   : clic sur un onglet (son libelle, toujours generique : Identite, Medical...) ;
//   - action   : clic sur un bouton ou une entree de menu reperes par leur data-testid, chiffres
//                retires (edit-sub, export-pdf-btn...) - jamais le texte d'un bouton, qui peut
//                contenir un nom ("Plus d'actions pour Jean DUPONT") ;
//   - presence : 30 s de presence ACTIVE (onglet visible et activite dans les 2 dernieres minutes),
//                pour mesurer le temps passe par page.
// Envoi par lots (POST /usage/events) ; le serveur n'ajoute que le role principal du compte.
// Le jeton de session est aleatoire, propre a l'onglet, sans lien avec le compte.
import React from 'react';
import { useLocation } from 'react-router-dom';

const API = `${import.meta.env.VITE_BACKEND_URL}/api`;
const PAS_PRESENCE = 30;          // secondes par evenement de presence
const INACTIVITE = 120 * 1000;    // au-dela, l'onglet ouvert ne compte plus comme temps passe
const ENVOI = 30 * 1000;

const PAGES = [
  [/^\/$/, 'Accueil'],
  [/^\/divers\/\d+/, 'Fiche plongeur'],
  [/^\/divers\/?$/, 'Adhérents'],
  [/^\/subscriptions\/\d+\/recu/, 'Reçu de paiement'],
  [/^\/subscriptions/, 'Adhésions'],
  [/^\/certificats/, 'Certificats médicaux'],
  [/^\/tresorerie/, 'Trésorerie'],
  [/^\/formations/, 'Formations'],
  [/^\/cursus/, 'Cursus'],
  [/^\/evenements\/\d+\/participants/, 'Participant à une sortie'],
  [/^\/evenements/, 'Événements'],
  [/^\/covoiturage/, 'Covoiturage'],
  [/^\/equipment\/stats/, 'Statistiques matériel'],
  [/^\/equipment/, 'Gestion du matériel'],
  [/^\/reservations/, 'Réservations de matériel'],
  [/^\/mon-materiel/, 'Réserver du matériel'],
  [/^\/mon-profil/, 'Mon profil'],
  [/^\/stats/, 'Statistiques club'],
  [/^\/users/, 'Utilisateurs'],
  [/^\/connexions/, 'Connexions et usage'],
  [/^\/gdpr-requests/, 'Demandes RGPD'],
  [/^\/admin/, 'Administration'],
  [/^\/documentation/, 'Documentation'],
  [/^\/scan\//, 'Page QR code matériel'],
];

export const nomPage = (pathname, diverId) => {
  if (diverId && pathname === `/divers/${diverId}`) return 'Ma fiche';
  const trouve = PAGES.find(([motif]) => motif.test(pathname));
  return trouve ? trouve[1] : 'Autre page';
};

const nouvelleSession = () => {
  const lettres = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const tirage = new Uint32Array(16);
  (window.crypto || window.msCrypto).getRandomValues(tirage);
  return Array.from(tirage, (n) => lettres[n % lettres.length]).join('');
};

const session = () => {
  try {
    let s = sessionStorage.getItem('cesam_usage_session');
    if (!s) { s = nouvelleSession(); sessionStorage.setItem('cesam_usage_session', s); }
    return s;
  } catch {
    return nouvelleSession();
  }
};

// data-testid -> nom de fonctionnalite : chiffres et identifiants retires.
const normaliserTestid = (t) => t.replace(/-\d+(?=-|$)/g, '').replace(/\d{3,}/g, '').replace(/-+$/, '').slice(0, 60);

/** Installe la mesure (Layout). `actif` faux en apercu de role ou de donnees de test. */
export const useMesureUsage = ({ actif, theme, diverId }) => {
  const { pathname } = useLocation();
  const etat = React.useRef({ file: [], page: null, activite: Date.now(), actif, theme });
  // Valeurs courantes lues au moment de l'evenement : un changement de theme (chargement des
  // preferences du compte) ne doit pas recreer les ecouteurs ni recompter la page ouverte.
  etat.current.actif = actif;
  etat.current.theme = theme;

  const envoyer = React.useCallback((finDePage = false) => {
    const lot = etat.current.file.splice(0, 100);
    if (!lot.length) return;
    let jeton = null;
    try { jeton = localStorage.getItem('token'); } catch { /* rien */ }
    if (!jeton) return;
    fetch(`${API}/usage/events`, {
      method: 'POST',
      keepalive: finDePage,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jeton}` },
      body: JSON.stringify({ session: session(), events: lot }),
    }).catch(() => { /* mesure au mieux : un lot perdu n'empeche rien */ });
  }, []);

  const ajouter = React.useCallback((evenement) => {
    if (!etat.current.actif) return;
    etat.current.file.push({ ...evenement, theme: etat.current.theme });
    if (etat.current.file.length >= 80) envoyer();
  }, [envoyer]);

  // Changement de page (et premiere page quand la mesure s'active, a la connexion)
  React.useEffect(() => {
    // Les pages hors session (connexion, mot de passe) ne comptent pas : la connexion fait
    // passer la mesure en actif un instant avant la redirection.
    if (/^\/(login|mot-de-passe-oublie|reinitialiser-mot-de-passe|adhesion-expiree)/.test(pathname)) return;
    const page = nomPage(pathname, diverId);
    etat.current.page = page;
    if (actif) ajouter({ type: 'page', page });
  }, [pathname, diverId, actif, ajouter]);

  // Clics : onglets et actions reperees
  React.useEffect(() => {
    if (!actif) return undefined;
    const surClic = (e) => {
      const cible = e.target instanceof Element ? e.target : null;
      if (!cible) return;
      const page = etat.current.page || nomPage(window.location.pathname, diverId);
      const onglet = cible.closest('[role="tab"]');
      if (onglet) {
        const libelle = (onglet.textContent || '').replace(/\s+/g, ' ').trim().replace(/\d+/g, '').trim();
        if (libelle) ajouter({ type: 'onglet', page, detail: libelle.slice(0, 40) });
        return;
      }
      const commande = cible.closest('button, a, [role="menuitem"], [role="menuitemradio"], [role="switch"], [role="radio"], [role="option"]');
      const testid = commande?.getAttribute('data-testid') || commande?.closest('[data-testid]')?.getAttribute('data-testid');
      if (testid) ajouter({ type: 'action', page, detail: normaliserTestid(testid) });
    };
    document.addEventListener('click', surClic, true);
    return () => document.removeEventListener('click', surClic, true);
  }, [actif, ajouter, diverId]);

  // Presence active, et envois reguliers
  React.useEffect(() => {
    if (!actif) return undefined;
    const activite = () => { etat.current.activite = Date.now(); };
    const evts = ['pointerdown', 'keydown', 'scroll', 'mousemove', 'touchstart'];
    evts.forEach(n => window.addEventListener(n, activite, { passive: true }));
    const presence = setInterval(() => {
      if (document.visibilityState === 'visible' && Date.now() - etat.current.activite < INACTIVITE && etat.current.page) {
        ajouter({ type: 'presence', page: etat.current.page, secondes: PAS_PRESENCE });
      }
    }, PAS_PRESENCE * 1000);
    const envoi = setInterval(() => envoyer(false), ENVOI);
    const cache = () => { if (document.visibilityState === 'hidden') envoyer(true); };
    const quitte = () => envoyer(true);
    document.addEventListener('visibilitychange', cache);
    window.addEventListener('pagehide', quitte);
    return () => {
      evts.forEach(n => window.removeEventListener(n, activite));
      clearInterval(presence); clearInterval(envoi);
      document.removeEventListener('visibilitychange', cache);
      window.removeEventListener('pagehide', quitte);
      envoyer(true);
    };
  }, [actif, ajouter, envoyer]);
};
